/**
 * React Query hooks for the Electre NG connector: the account administrators
 * configure, and the baskets librarians send from Electre.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "@/api/client";
import type {
  ElectreBasketImportResult,
  ElectreBasketsResponse,
  ElectreSettings,
  ElectreSettingsInput,
  ElectreTestResult,
} from "@/api/types";

const SETTINGS_KEY = ["electre", "settings"];
const BASKETS_KEY = ["electre", "baskets"];

export function useElectreSettings() {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => api.get<ElectreSettings>("/admin/electre"),
  });
}

export function useSaveElectreSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ElectreSettingsInput) => api.put<ElectreSettings>("/admin/electre", input),
    onSuccess: (settings) => {
      queryClient.setQueryData(SETTINGS_KEY, settings);
      void queryClient.invalidateQueries({ queryKey: BASKETS_KEY });
    },
  });
}

export function useClearElectreSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.del<ElectreSettings>("/admin/electre"),
    onSuccess: (settings) => {
      queryClient.setQueryData(SETTINGS_KEY, settings);
      void queryClient.invalidateQueries({ queryKey: BASKETS_KEY });
    },
  });
}

export function useTestElectreConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<ElectreTestResult>("/admin/electre/test"),
    onSettled: () => {
      // The test records its outcome (last status, error) on the source
      void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

export function useElectreBaskets() {
  return useQuery({
    queryKey: BASKETS_KEY,
    queryFn: () => api.get<ElectreBasketsResponse>("/electre/baskets"),
    // Electre is a paid third-party service: do not poll it on every focus
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useImportElectreBasket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dryRun }: { id: string; dryRun: boolean }) =>
      api.post<ElectreBasketImportResult>(
        `/electre/baskets/${encodeURIComponent(id)}/import${dryRun ? "?dry_run=1" : ""}`
      ),
    onSuccess: (_result, { dryRun }) => {
      if (!dryRun) {
        void queryClient.invalidateQueries({ queryKey: BASKETS_KEY });
      }
    },
  });
}
